<template>
    <div class="application-view">
        <card-list >
            <subheader-list-item title="Application" icon="fa-file-certificate" />


            <data-row title="Full Name">{{ applicant.users.first_name }} {{ applicant.users.last_name }}</data-row>
            <data-row title="Certificate" v-if="application.cert">{{ application.cert.title }}</data-row>
            <data-row title="Provider">{{ application.organization.title }}</data-row>
            <data-row title="Conference" v-if="applicant.conf_applications">{{ applicant.conf_applications.conferences.title }}</data-row>
            <data-row title="Course" v-if="applicant.course_applications">{{ applicant.course_applications.courses.title }}</data-row>
            <data-row title="Units" v-if="applicant.total_units && applicant.conf_applications">{{applicant.total_units}}<span v-if="applicant.conf_applications.max_units"> of {{ applicant.conf_applications.max_units }} maximum</span> {{application.cert.certification.unit_title}}</data-row>
            <data-row title="Units" v-if="applicant.total_units && applicant.course_applications">{{applicant.total_units}}<span v-if="applicant.course_applications.max_units"> of {{ applicant.course_applications.max_units }} maximum</span> {{applicant.course_applications.applications.certification_certs.certifications.unit_title}}</data-row>
            <data-row title="Minutes" v-if="applicant.conf_applications">{{ applicant.total_minutes }}<span v-if="applicant.conf_applications.min_minutes_required"> of {{ applicant.conf_applications.min_minutes_required }} minimum required</span></data-row>
            <data-row title="Sessions" v-if="applicant.conf_applications">{{ getTotalSessions() }} recorded<span v-if="applicant.conf_applications.min_sessions_required"> of {{ applicant.conf_applications.min_sessions_required }} minimum required</span></data-row>
            <data-row title="Paid" v-if="currentUser.isAdmin || currentUser.isSuper">{{ applicant.total_paid | currency }} of {{ applicant.quoted_price | currency }} quoted</data-row>
            <data-row title="Started">{{applicant.created_at | moment('MMMM Do YYYY, h:mm:ss a')}}</data-row>
            <data-row title="Progress">
                <i class="fas fa-exclamation-triangle has-text-warning fa-fw" v-if="applicant.progress < lastStepCount"></i><i class="fas fa-check has-text-success fa-fw" v-else></i>
                Step {{ Math.min(lastStepCount, applicant.progress) }} of {{lastStepCount}}
            </data-row>
            <data-row title="Completed" v-if="applicant.progress >= lastStepCount">{{applicant.completed_at | moment('MMMM Do YYYY, h:mm:ss a')}}</data-row>

            <template v-if="application.outline" v-for="outline in application.outline">

                <subheader-list-item :title="outline.title" icon="fa-list-alt" />

                <div
                    class="form-data"
                    v-for="block in getOutlineContent(outline).content"
                >
                    <card-list-item v-if="block.component === 'CheckBox'" no-hover>
                        <template v-slot:icon><i class="far fa-fw fa-toggle-off"></i></template>
                        <template v-slot:content>
                            <div class="meta">
                                {{block.content.option}}
                            </div>
                            <div class="tile-title mt-1">{{ getAppData(block.content.id) }}</div>
                        </template>
                    </card-list-item>

                    <card-list-item v-if="block.component === 'DropdownField'" no-hover>
                        <template v-slot:icon><i class="far fa-fw fa-square-caret-down"></i></template>
                        <template v-slot:content>
                            <div class="meta" v-if="block.content.areas[0].content">
                                <render-editor-js-application :content="block.content.areas[0].content" :component="{}" />
                            </div>
                            <div class="tile-title mt-1">{{ getAppData(block.content.id) }}</div>
                        </template>
                    </card-list-item>

                    <card-list-item v-else-if="block.component === 'TextField' || block.component === 'TextArea'" no-hover>
                        <template v-slot:icon><i class="far fa-fw fa-pen-field"></i></template>
                        <template v-slot:content>
                            <div class="meta" v-if="block.content.areas[0].content">
                                <render-editor-js-application :content="block.content.areas[0].content" :component="{}" />
                            </div>
                            <div class="tile-title mt-1">{{ getAppData(block.content.id) }}</div>
                        </template>
                    </card-list-item>

                    <card-list-item v-else-if="block.component === 'DistrictSchoolField'" no-hover>
                        <template v-slot:icon><i class="far fa-fw fa-school"></i></template>
                        <template v-slot:content>
                            <div class="meta" v-if="block.content.areas[0].content">
                                <render-editor-js-application :content="block.content.areas[0].content" :component="{}" />
                            </div>
                            <div class="tile-title mt-1" v-if="getAppData(block.content.id)">
                                District: {{ getAppData(block.content.id).district }}
                                <span class="has-text-grey" v-if="getAppData(block.content.id).district_id">(#{{ getAppData(block.content.id).district_id }})</span>
                                <br/>
                                School: {{ getAppData(block.content.id).school }}
                                <span class="has-text-grey" v-if="getAppData(block.content.id).school_id">(#{{ getAppData(block.content.id).school_id }})</span>
                            </div>
                        </template>
                    </card-list-item>

                </div>

                <card-list-item v-if="getOutlineContent(outline).attributes.require_signature" no-hover>
                    <template v-slot:icon><i class="far fa-fw fa-signature"></i></template>
                    <template v-slot:content>
                        <div class="meta">
                            {{getOutlineContent(outline).attributes.signature_clause}}
                        </div>
                        <div class="tile-title mt-1">
                            <img :src="getSignature(getOutlineContent(outline).attributes.id)" class="mt-3 signature" style="max-width: 100%;"/>
                        </div>
                    </template>
                </card-list-item>

            </template>

            <subheader-list-item title="Study Log (snapshot)" icon="fa-clipboard" />

            <card-list-item v-for="entry in log" no-hover>
                <template v-slot:icon><i class="far fa-fw"></i></template>
                <template v-slot:content>
                    <div class="tile-title">{{ entry.title }}</div>
                    <div class="meta mt-1">{{ displayDates(entry.start_at, entry.end_at) }} ({{ entry.minutes }} min)</div>
                </template>
            </card-list-item>

            <subheader-list-item title="Application History" icon="fa-timer" />

            <card-list-item v-for="entry in applicant.reg_application_log" no-hover>
                <template v-slot:icon><i class="far fa-fw" :class="{'fa-user': entry.user_id, 'fa-user-tie': entry.portal_user_id}"></i></template>
                <template v-slot:content>
                    <div class="tile-title">{{ entry.action }}</div>
                    <div class="meta mt-1" v-if="entry.users">{{ entry.users.first_name }} {{entry.users.last_name}}</div>
                    <div class="meta mt-1" v-if="entry.portal_users">{{ entry.portal_users.first_name }} {{entry.portal_users.last_name}}</div>
                    <div class="meta">{{ entry.created_at | moment('MMMM Do YYYY, h:mm:ss a')}}</div>
                </template>
            </card-list-item>
        </card-list>

    </div>
</template>

<script>
    import RenderEditorJsApplication from "./EditorJs/Render.vue";
    import CardListItem from "../../../TIER/components/CardListItem.vue";
    import DataRow from "../../../TIER/components/DataRow.vue";
    import moment from "moment";
    import {mapGetters} from "vuex";
    import CardList from "../../../TIER/components/CardList.vue";
    import SubheaderListItem from "../../../TIER/components/SubheaderListItem.vue";

    export default {
        name: "ApplicationView",
        components: {SubheaderListItem, CardList, DataRow, CardListItem, RenderEditorJsApplication},
        props: ['application', 'applicant'],
        computed: {
            ...mapGetters(['currentUser']),
            lastStepCount() {
                return this.application.outline.length + ((this.applicant.quoted_price) ? 2 : 1);
            },
            log() {
                let log = this.applicant.log_snapshot;
                if (this.applicant.course_applications) {
                    log.forEach(entry => {
                        entry.start_at = entry.start_date;
                        entry.end_at = entry.end_date;
                        entry.minutes = entry.mins;
                    });
                }
                return log.filter(entry => entry.minutes > 0);
            }
        },
        methods: {
            getOutlineContent(outline) {
                return JSON.parse(outline.component.content);
            },
            getAppData(id) {
                if (this.applicant.enrollment_id) {
                    return (this.applicant.enrollment_application_data.find(d => d.field === id)) ? this.applicant.enrollment_application_data.find(d => d.field === id).value : '';
                } else if (this.applicant.registration_id) {
                    return (this.applicant.reg_application_data.find(d => d.field === id)) ? this.applicant.reg_application_data.find(d => d.field === id).value : '';
                }
            },
            getSignature(id) {
                //todo, the way the data is saved isn't the best, but this should work for now.  Typically 'field' would be the id
                if (this.applicant.conf_applications) {
                    let sig = this.applicant.reg_application_data.find(d => d.field === 'signature' && d.block.id === id);
                    return (sig) ? sig.value.signature : '';
                } else if (this.applicant.course_applications) {
                    let sig = this.applicant.enrollment_application_data.find(d => d.field === 'signature' && d.block.id === id);
                    return (sig) ? sig.value.signature : '';
                }
            },
            getHistoryCount(id) {
                // todo, this isn't in use but it does show the total number of entries for a given field
                return (this.applicant.reg_application_data.filter(d => d.field === id)).length;
            },
            getTotalSessions() {
                let titles = [];
                this.applicant.log_snapshot.forEach(entry => {
                    if (!titles.includes(entry.title)) titles.push(entry.title);
                });
                return titles.length;
            },
            displayDates: function(start, end) {
                let s = moment(start);
                let e = moment(end);
                if (s.isSame(e, 'day')) {
                    return s.format('dddd, MMMM Do, h:mm A') + ' - ' + e.format('h:mm A');
                } else {
                    return s.format('MMM Do, h:mm A') + ' - ' + e.format('MMM Do, h:mm A');
                }
            },
        }
    }
</script>

<style scoped lang="scss">

</style>

<style>
    .application-view {
        h1, h2, h3, h4, h5 {
            font-size: 1rem !important;
        }
        .render-header {
            padding: 0 !important;


        }
        .block {
            margin-bottom: 10px !important;
        }

        .form-data {

        }
    }


</style>
